// Island sounds offline (WI-24, L6-09): the active trip's places' picked sounds and voice clips are
// warmed into the service worker's `place-audio` cache (their lists into `place-lists`) in the
// background, so a tap on the island
// isn't silent offline. Only the trip's own places (its stages' `spot`s, so any region's trip works
// the same), only the voice in use, and only after the first load has settled (it never competes
// with the shell). Settings shows how far it got.
import { useEffect } from 'react'
import { create } from 'zustand'
import type { PicksFile, VoiceManifest } from '../map/placekit/audio'
import { usePlacePrefs } from '../map/placePrefs'
import { assetDir } from '../map/places/registry'

/** The service worker's runtime cache for these files (src/sw.ts): it serves them cache-first. */
export const PLACE_AUDIO_CACHE = 'place-audio'
/** After the first load: the shell, the pack and the stage go first. */
const START_MS = 4000
const AT_ONCE = 3

export interface PrefetchState {
  /** Files the trip's places use (0: nothing to fetch, or not started). */
  total: number
  /** Of those, in the cache. */
  done: number
  running: boolean
}

export const usePlaceAudioPrefetch = create<PrefetchState>(() => ({ total: 0, done: 0, running: false }))

/** The service worker's cache for the lists themselves (sound-picks.json, manifest.json). */
export const PLACE_LISTS_CACHE = 'place-lists'

/** A list, kept in the lists cache too: offline, an island needs its list to find its files. */
async function json<T>(url: string): Promise<T | null> {
  try {
    const r = await fetch(url)
    if (!r.ok || !(r.headers.get('content-type') ?? '').includes('json')) return null
    if (typeof caches !== 'undefined') {
      const copy = r.clone()
      await caches
        .open(PLACE_LISTS_CACHE)
        .then((c) => c.put(new URL(url, location.origin).href, copy))
        .catch(() => undefined)
    }
    return (await r.json()) as T
  } catch {
    return null
  }
}

/** A place's sound files and its clips in `voice` (null: the manifest's default; 'off': none). */
export async function placeAudioFiles(id: string, voice: string | null): Promise<string[]> {
  const dir = assetDir(id)
  const [p, v] = await Promise.all([json<PicksFile>(`/places/${dir}/sound-picks.json`), json<VoiceManifest>(`/voices/${dir}/manifest.json`)])
  const files = Object.values(p?.sounds ?? {}).flatMap((s) => (s.file ? [s.file] : []))
  if (v && voice !== 'off') {
    const sp = voice && v.speakers[voice] ? voice : v.default
    for (const clip of Object.values(v.clips)) if (clip[sp]) files.push(clip[sp])
  }
  return files
}

let busy = false

/** Fetches what the cache lacks, a few at a time. Safe to call again: cached files are skipped. */
export async function prefetchPlaceAudio(ids: readonly string[], signal?: AbortSignal): Promise<void> {
  if (busy || !ids.length || typeof caches === 'undefined' || navigator.onLine === false) return
  if ((navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData) return
  busy = true
  try {
    const voice = usePlacePrefs.getState().voice
    const files = [...new Set((await Promise.all(ids.map((id) => placeAudioFiles(id, voice)))).flat())]
    const cache = await caches.open(PLACE_AUDIO_CACHE)
    const url = (f: string) => new URL(`/${f}`, location.origin).href
    const missing: string[] = []
    for (const f of files) if (!(await cache.match(url(f)))) missing.push(f)
    usePlaceAudioPrefetch.setState({ total: files.length, done: files.length - missing.length, running: missing.length > 0 })
    let next = 0
    const lane = async () => {
      while (next < missing.length && !signal?.aborted) {
        const u = url(missing[next++]!)
        try {
          const res = await fetch(u, { signal })
          // Real files only, like the service worker's own guard (never an HTML page).
          if (res.status !== 200 || (res.headers.get('content-type') ?? '').includes('text/html')) continue
          await cache.put(u, res)
          usePlaceAudioPrefetch.setState((s) => ({ done: s.done + 1 }))
        } catch {
          // offline or aborted: the next run picks it up
        }
      }
    }
    await Promise.all(Array.from({ length: AT_ONCE }, lane))
  } catch {
    // no cache storage (private mode): sounds still load when played
  } finally {
    busy = false
    usePlaceAudioPrefetch.setState({ running: false })
  }
}

/** Warms the trip's places (ids joined by commas) after the first load, and again on reconnecting. */
export function usePrefetchPlaceAudio(key: string) {
  useEffect(() => {
    if (!key) return
    const ctl = new AbortController()
    const run = () => void prefetchPlaceAudio(key.split(','), ctl.signal)
    const timer = setTimeout(run, START_MS)
    addEventListener('online', run)
    return () => {
      ctl.abort()
      clearTimeout(timer)
      removeEventListener('online', run)
    }
  }, [key])
}
